import React, { useEffect, useMemo } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlineArrowCircleDown, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord, updateApiStatus } from 'services/InvoiceService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { numberFormat } from 'components/helper'
import dayjs from 'dayjs'
import { GrDocumentVerified } from 'react-icons/gr'
const { REACT_APP_URL } = process.env
injectReducer('uploadInvoices', reducer)

export default function UploadInvoices() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.uploadInvoices.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        tableData.service,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleStatus = (id, data) => {
        updateApiStatus(id, data)
            .then(() => {
                Success('Reconciliation initiated successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment_details?.company_name,
            },
            {
                field: 'client',
                header: 'Client Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment_details?.client,
            },
            {
                field: 'transport_provider_name',
                header: 'Transport Provider',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.assignment_details?.transport_provider_name,
            },
            {
                field: 'invoice',
                header: 'Invoice',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice.split('/')[row?.invoice.split('/').length - 1],
            },
            {
                field: 'invoice_date',
                header: 'Invoice Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_date
                        ? dayjs(row?.invoice_date).format('DD-MMM-YYYY')
                        : '-',
            },
            {
                field: 'invoice_amount',
                header: 'Invoice Total',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.invoice_amount, 'AUD'),
            },
            {
                field: 'uploaded_at',
                header: 'Uploaded At',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.uploaded_at
                        ? dayjs(row?.uploaded_at).format('DD-MMM-YYYY')
                        : '-',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            <Tooltip title={'Verify'} placement={'top'}>
                                <span
                                    className={`cursor-pointer p-2 hover:text-primary`}
                                    onClick={() =>
                                        handleStatus(row.id, { status: true })
                                    }
                                >
                                    <GrDocumentVerified size={20} />
                                </span>
                            </Tooltip>
                            <Tooltip title={'Download Invoice'}>
                                <span className="hover:text-primary">
                                    <a
                                        href={`${REACT_APP_URL}${row.invoice}`}
                                        download={
                                            row.invoice.split('/')[
                                                row.invoice.split('/').length -
                                                    1
                                            ]
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <HiOutlineArrowCircleDown size={20} />
                                    </a>
                                </span>
                            </Tooltip>
                            {checkPermission('extra-charges', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            tableData.service,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Upload Invoices
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('upload-invoices', 'create')
                        ? '/invoices/upload-invoices/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
